import React, { useEffect } from 'react';
import { NumberField } from '@flos/react-ui';
import useAppState from '../../state/appState';
import { Numberfield as NumberfieldType, FormValues, FormConfig } from '@topdanmark-onlinesolutions/claim-form-types';
import { getIsValid, getValidationErrors } from '../../validators/validator-mapping';
import { ElementValue } from '../../types/form-values';
import { ClaimFormSubmitFieldValue } from '../../types/claim-form-submit-field-value';
import { SummaryRowTemplate } from '../../helpers/templates';
import { ElementValueHuman } from '../../types/element-values';
import { isRequired } from '../../helpers/isRequired';
import { getValue } from '../values/Get';
import { getElementFromElementId } from '../../helpers/getElementFromElementId';
import { getMinMaxLength } from '../../helpers/getMinMaxLength';
import { getClaimFormSubmitValue } from '../../helpers/getClaimFormSubmitValue';

const Numberfield = ({ elementId, caption, placeholder, helpText, allowNegative }: NumberfieldType) => {
    const formConfig = useAppState((state) => state.formConfig);
    const formValues = useAppState((state) => state.formValues);
    const setFormValue = useAppState((state) => state.setFormValue);

    const value = Numberfield.getValueOrDefault(formValues, elementId);

    const [errorMessage, setErrorMessage] = React.useState('');

    const [fieldHasHadFocusAtLeastOnce, setFieldHasHadFocusAtLeastOnce] = React.useState(false);

    const doValidation = () => {
        if (!formConfig) {
            return;
        }

        if (!fieldHasHadFocusAtLeastOnce) {
            return;
        }

        const errorMessages = getValidationErrors(formConfig, formValues, elementId);
        setErrorMessage(errorMessages[0]);
    };

    useEffect(() => {
        doValidation();
        // eslint-disable-next-line
    }, [value]);

    const isRequiredField = isRequired(formConfig, elementId);

    const isValid = Numberfield.isValidField(formConfig, formValues, elementId, fieldHasHadFocusAtLeastOnce);
    const lengthObject = getMinMaxLength(formConfig, elementId);
    const { minLength, maxLength } = lengthObject;

    return (
        <>
            {caption && <h6>{caption}</h6>}
            <NumberField
                value={value}
                onBlur={() => {
                    doValidation();
                }}
                onFocus={() => {
                    setFieldHasHadFocusAtLeastOnce(true);
                }}
                onValueChange={(valueObject) => {
                    const value = valueObject.value;
                    setFormValue(elementId, value);
                }}
                isValid={isValid}
                errorText={errorMessage}
                id={elementId}
                key={elementId}
                label={placeholder}
                helpText={helpText}
                required={isRequiredField}
                aria-label={caption}
                data-testid="numberfield"
                minLength={minLength}
                maxLength={maxLength}
                allowNegative={allowNegative}
            />
        </>
    );
};

Numberfield.ResumePage = (formConfig: FormConfig, formValues: FormValues, elementId: string): JSX.Element => {
    const element = getElementFromElementId(formConfig, elementId) as NumberfieldType;
    const { summaryCaption } = element;

    const value = getValue(formConfig, formValues, elementId);

    // Happens when the user has not filled
    // out previous pages but somehow navigated to the resume page
    if (value === undefined) {
        return <></>;
    }

    return <SummaryRowTemplate elementId={elementId} caption={summaryCaption} value={value} />;
};

Numberfield.getValueSubmit = (formValues: FormValues, elementId: string, label: string): ClaimFormSubmitFieldValue => {
    const valueObj = Numberfield.getValue(formValues, elementId);
    const valueStr = Numberfield.getValueHuman(formValues, elementId);

    const valueId = { string: valueObj };

    return getClaimFormSubmitValue('numberfield', elementId, label, valueStr, valueId);
};

Numberfield.getValue = (formValues: FormValues, elementId: string): ElementValue => {
    return formValues[elementId];
};

Numberfield.getValueOrDefault = (formValues: FormValues, elementId: string) => {
    const value = Numberfield.getValue(formValues, elementId);
    if (value === undefined) {
        return '';
    }
    return value;
};

Numberfield.getValueHuman = (formValues: FormValues, elementId: string): ElementValueHuman => {
    const valueObj = Numberfield.getValue(formValues, elementId);

    const valueStr = valueObj;

    return valueStr;
};

Numberfield.isValid = (formConfig: FormConfig, formValues: FormValues, elementId: string): boolean | undefined => {
    return getIsValid(formConfig, formValues, elementId);
};

Numberfield.isValidField = (
    formConfig: FormConfig | undefined,
    formValues: FormValues,
    elementId: string,
    fieldHadFocusAtLeastOnce: boolean
): boolean | undefined => {
    if (!formConfig) {
        return undefined;
    }

    if (!fieldHadFocusAtLeastOnce) {
        return undefined;
    }

    //const value = Numberfield.getValue(formValues, elementId);

    // When the user has not typed anything,
    // undefined is returned to prevent
    // showing red errors all over as soon
    // as he sees the form
    // if (value === '' || value === undefined) {
    //     return undefined;
    // }

    const isValid = Numberfield.isValid(formConfig, formValues, elementId);

    return isValid;
};

export default Numberfield;
